import { useEffect, useState } from 'react';
import { GIFT_CAMPAIGN, GIFT_TIMER_STORAGE_KEY } from './constants';

const readStoredExpiry = (
  key: string = GIFT_TIMER_STORAGE_KEY
): number | null => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeStoredExpiry = (key: string, value: number) => {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    /* noop — sandboxed iframe / privacy mode */
  }
};

const ensureExpiry = (
  active: boolean,
  timerMinutes: number = GIFT_CAMPAIGN.timerMinutes,
  storageKey: string = GIFT_TIMER_STORAGE_KEY
): number | null => {
  if (!active) return null;
  const existing = readStoredExpiry(storageKey);
  const now = Date.now();
  // Reuse existing only if not expired yet
  if (existing && existing > now) {
    return existing;
  }
  if (existing) {
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      console.log('Failed to remove stored expiry');
    }
  }
  const fresh = now + timerMinutes * 60 * 1000;
  writeStoredExpiry(storageKey, fresh);
  return fresh;
};

export interface GiftTimerState {
  active: boolean;
  expiresAt: number | null;
  remainingMs: number;
  expired: boolean;
  mmss: string;
  warning: boolean;
}

const formatMMSS = (ms: number): string => {
  if (ms <= 0) return '00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const m = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, '0');
  const s = (totalSeconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

export const useGiftTimer = (
  active: boolean,
  timerMinutes: number = GIFT_CAMPAIGN.timerMinutes,
  storageKey: string = GIFT_TIMER_STORAGE_KEY
): GiftTimerState => {
  const [expiresAt, setExpiresAt] = useState<number | null>(() =>
    ensureExpiry(active, timerMinutes, storageKey)
  );
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    setExpiresAt(ensureExpiry(active, timerMinutes, storageKey));
  }, [active, timerMinutes, storageKey]);

  useEffect(() => {
    if (!active || !expiresAt) return undefined;
    const id = window.setInterval(() => {
      const currentTime = Date.now();
      setNow(currentTime);
      if (currentTime >= expiresAt) {
        if (readStoredExpiry(storageKey)) {
          try {
            window.localStorage.removeItem(storageKey);
          } catch {}
        }
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [active, expiresAt, storageKey]);

  const remainingMs = expiresAt ? Math.max(0, expiresAt - now) : 0;
  const expired = !!expiresAt && remainingMs <= 0;
  const warning = remainingMs > 0 && remainingMs < 5 * 60 * 1000;

  return {
    active,
    expiresAt,
    remainingMs,
    expired,
    warning,
    mmss: formatMMSS(remainingMs),
  };
};
